import { useCallback, useEffect, useState } from 'react';

interface UseAccordionStateOptions {
  /** Unique identifier for this accordion instance */
  id: string;
  /** Default open state */
  defaultOpen?: string | undefined;
  /** Whether to persist state across page navigations */
  persist?: boolean;
}

interface UseAccordionStateReturn {
  /** Currently open accordion value */
  openValue: string | undefined;
  /** Function to handle accordion value changes */
  handleValueChange: (value: string) => void;
  /** Function to manually set accordion state */
  setOpenValue: (value: string | undefined) => void;
}

const STORAGE_KEY_PREFIX = 'accordion-state-';

/**
 * Custom hook for managing accordion state with optional persistence
 */
export const useAccordionState = ({
  id,
  defaultOpen,
  persist = true,
}: UseAccordionStateOptions): UseAccordionStateReturn => {
  const storageKey = `${STORAGE_KEY_PREFIX}${id}`;

  // Initialize state from localStorage or default
  const [openValue, setOpenValueState] = useState<string | undefined>(() => {
    if (!persist) return defaultOpen;

    try {
      const saved = localStorage.getItem(storageKey);
      return saved || defaultOpen;
    } catch {
      return defaultOpen;
    }
  });

  // Persist state changes to localStorage
  const setOpenValue = useCallback(
    (value: string | undefined) => {
      setOpenValueState(value);

      if (persist) {
        try {
          if (value) {
            localStorage.setItem(storageKey, value);
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch {
          // Silently fail if localStorage is not available
        }
      }
    },
    [persist, storageKey],
  );

  // Handle accordion value changes
  const handleValueChange = useCallback(
    (value: string) => {
      setOpenValue(value || undefined);
    },
    [setOpenValue],
  );

  // Clear state when component unmounts (optional cleanup)
  useEffect(() => {
    return () => {
      // Only clear if explicitly requested or for cleanup scenarios
      // This prevents accidental state loss
    };
  }, []);

  return {
    openValue,
    handleValueChange,
    setOpenValue,
  };
};
